/**
 * LibraryQuickDrop — admin-only "Spotify-style" drag-drop overlay.
 *
 * Mounted at the top of /library for admins/writers. When the user drags
 * audio (mp3/wav/m4a/...) or video (mp4/webm/...) files anywhere on the
 * page, a full-screen overlay appears: "Drop here, then pick a series."
 * After the drop, a small picker lists every audiobook + cinematic series
 * the user owns (admins see all). Choosing one opens AddEpisodeModal with
 * the dropped files preloaded — no clicks, no auto-numbering math.
 *
 * Hits no new endpoints — reuses POST /api/cinematic/series/{id}/episode
 * and POST /api/audiobook/series/{id}/chapter through AddEpisodeModal.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Film, Headphones, X, Upload as UploadIcon } from 'lucide-react';
import { Button } from './ui/button';
import AddEpisodeModal from './AddEpisodeModal';
import { authFetch } from '../utils/authUtils';

const ACCEPTED_MEDIA = /^(audio|video)\//;

const LibraryQuickDrop = ({ enabled = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [overlay, setOverlay] = useState(false);
  const [stagedFiles, setStagedFiles] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [allSeries, setAllSeries] = useState([]);
  const [chosenSeries, setChosenSeries] = useState(null);
  const [chosenEpisodes, setChosenEpisodes] = useState([]);
  const dragDepth = useRef(0);

  // Wire global dragenter / dragleave / dragover / drop. We use a depth
  // counter so the overlay doesn't flicker when the cursor crosses child
  // element boundaries inside the page.
  useEffect(() => {
    if (!enabled) return undefined;

    const onDragEnter = (e) => {
      // Only react to file drags — ignore text/HTML dragging within the page.
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
      e.preventDefault();
      dragDepth.current += 1;
      setOverlay(true);
    };
    const onDragOver = (e) => {
      if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
        e.preventDefault();
      }
    };
    const onDragLeave = (e) => {
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setOverlay(false);
    };
    const onDrop = (e) => {
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
      e.preventDefault();
      dragDepth.current = 0;
      setOverlay(false);
      const files = Array.from(e.dataTransfer.files || []).filter((f) =>
        ACCEPTED_MEDIA.test(f.type)
      );
      if (!files.length) return;
      setStagedFiles(files);
      setPickerOpen(true);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [enabled]);

  // Fetch series list when the picker opens. We pull from /api/library which
  // is already role-filtered, so admins see all series + writers see theirs.
  useEffect(() => {
    if (!pickerOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/library`);
        if (!r.ok) return;
        const d = await r.json();
        const list = (d.series || []).filter(
          (s) => s.type === 'cinematic' || s.type === 'audiobook' || s.content_type === 'cinematic' || s.content_type === 'audiobook'
        );
        if (!cancelled) setAllSeries(list);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [pickerOpen, BACKEND_URL]);

  // When a series is picked, fetch its existing episodes so AddEpisodeModal's
  // sequential numbering starts at the next slot instead of restarting at 1.
  const pickSeries = async (s) => {
    setChosenSeries(s);
    try {
      const isCine = (s.type || s.content_type) === 'cinematic';
      const path = isCine
        ? `/api/cinematic/series/${s.series_id}/episodes`
        : `/api/audiobook/series/${s.series_id}/chapters`;
      const r = await authFetch(`${BACKEND_URL}${path}`);
      if (r.ok) {
        const d = await r.json();
        const eps = d.episodes || d.chapters || [];
        setChosenEpisodes(eps);
      } else {
        setChosenEpisodes([]);
      }
    } catch {
      setChosenEpisodes([]);
    }
    setPickerOpen(false);
  };

  const reset = () => {
    setStagedFiles(null);
    setPickerOpen(false);
    setChosenSeries(null);
    setChosenEpisodes([]);
  };

  if (!enabled) return null;

  return (
    <>
      {/* Full-screen drop overlay — visible while dragging files over the page */}
      {overlay && (
        <div
          className="fixed inset-0 z-[55] bg-indigo-900/40 backdrop-blur-sm flex items-center justify-center pointer-events-none"
          data-testid="library-quick-drop-overlay"
        >
          <div className="bg-slate-900/95 border-2 border-dashed border-indigo-400 rounded-2xl p-10 text-center shadow-2xl">
            <UploadIcon className="w-12 h-12 mx-auto mb-3 text-indigo-300" />
            <p className="text-xl font-bold text-white">Drop to add to a series</p>
            <p className="text-sm text-gray-400 mt-1">
              Audio + video files will be batched in one upload.
            </p>
          </div>
        </div>
      )}

      {/* Series picker — appears after the user drops files */}
      {pickerOpen && stagedFiles && (
        <div
          className="fixed inset-0 z-[58] bg-black/70 flex items-center justify-center p-4"
          data-testid="library-quick-drop-picker"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-800">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-indigo-300 font-semibold">
                  Quick Upload
                </p>
                <p className="text-base font-bold text-white mt-0.5">
                  {stagedFiles.length} file{stagedFiles.length === 1 ? '' : 's'} ready · Pick a series
                </p>
              </div>
              <button
                type="button"
                onClick={reset}
                className="text-gray-400 hover:text-white p-1"
                data-testid="library-quick-drop-cancel"
                aria-label="Cancel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {allSeries.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">
                  No series found. Create one first via Upload → Cinematic / Audiobook series.
                </div>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {allSeries.map((s) => {
                    const isCine = (s.type || s.content_type) === 'cinematic';
                    const SIcon = isCine ? Film : Headphones;
                    return (
                      <li key={s.series_id}>
                        <button
                          type="button"
                          onClick={() => pickSeries(s)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/70 transition-colors text-left"
                          data-testid={`quick-drop-series-${s.series_id}`}
                        >
                          <div
                            className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isCine
                                ? 'bg-rose-500/20 text-rose-300'
                                : 'bg-purple-500/20 text-purple-300'
                            }`}
                          >
                            <SIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-white truncate">
                              {s.title}
                            </p>
                            <p className="text-[11px] text-gray-400 truncate">
                              {isCine ? 'Cinematic' : 'Audiobook'}
                              {s.episode_count != null
                                ? ` · ${s.episode_count} ${isCine ? 'episodes' : 'chapters'}`
                                : ''}
                            </p>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="p-3 border-t border-slate-800 flex justify-end">
              <Button
                variant="ghost"
                onClick={reset}
                className="text-gray-300 hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Once a series is picked, hand off to the existing AddEpisodeModal
          with prefilledFiles — admin reviews titles + numbers + clicks Upload. */}
      {chosenSeries && stagedFiles && (
        <AddEpisodeModal
          series={chosenSeries}
          existingEpisodes={chosenEpisodes}
          prefilledFiles={stagedFiles}
          onClose={reset}
          onUploaded={() => {
            // Library refresh is handled by SeriesFolder/Library when the modal
            // closes; just reset our local state.
            reset();
          }}
        />
      )}
    </>
  );
};

export default LibraryQuickDrop;
